import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { SaveConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  User,
  Mail,
  Phone,
  Shield,
  Key,
  Bell,
  Camera,
  Save,
  Lock,
  Smartphone,
  History,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Edit,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth'
import { getUserService } from '@/domain/user'
import { TwoFactorAuthService } from '@/domain/two-factor-auth/two-factor-auth.service'
import { DeviceService } from '@/domain/device/device.service'
import type { UserDevice, LoginHistory } from '@/domain/device/device.types'

// Map user type to display text
const getUserTypeDisplay = (type: string) => {
  const typeMap: Record<string, string> = {
    'super_admin': 'Super Admin',
    'admin': 'Admin',
    'customer': 'Customer',
  };
  return typeMap[type] || type;
};

export function ProfilePage() {
  const { t } = useTranslation()
  const { user: authUser, sessionToken, refreshUser, logout } = useAuth()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(() => {
    // ✅ ตรวจสอบ query parameter สำหรับ auto-select tab
    const tab = searchParams.get('tab')
    return tab && ['general', 'security', 'notifications', 'sessions'].includes(tab) ? tab : 'general'
  })
  
  // ✅ Auto-select tab จาก query parameter
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && ['general', 'security', 'notifications', 'sessions'].includes(tab)) {
      setActiveTab(tab)
    }
  }, [searchParams])
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false)
  const [showEnable2FADialog, setShowEnable2FADialog] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  
  // 2FA state
  const [twoFactorQRCode, setTwoFactorQRCode] = useState<string | null>(null)
  const [twoFactorSecret, setTwoFactorSecret] = useState<string | null>(null)
  const [twoFactorVerificationCode, setTwoFactorVerificationCode] = useState('')
  const [isGeneratingQR, setIsGeneratingQR] = useState(false)
  const [isVerifying2FA, setIsVerifying2FA] = useState(false)
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)

  // Load real user data from localStorage
  const [userData, setUserData] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    phone: string;
    lineId: string;
    role: string;
    status: string;
    avatar: string | null;
    createdAt: string;
    updatedAt: string;
    lastLogin: string;
    twoFactorEnabled: boolean;
    emailNotifications: boolean;
    pushNotifications: boolean;
    smsNotifications: boolean;
    language: string;
    timezone: string;
  }>({
    id: '',
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    lineId: '',
    role: '',
    status: '',
    avatar: null,
    createdAt: '',
    updatedAt: '',
    lastLogin: '',
    twoFactorEnabled: false,
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    language: 'th',
    timezone: 'Asia/Bangkok',
  })

  // 2FA Config state (for checking if admin is required to use 2FA)
  const [twoFactorConfig, setTwoFactorConfig] = useState<{
    enable2FA: boolean
    require2FAForAdmin: boolean
  }>({
    enable2FA: false,
    require2FAForAdmin: false,
  })

  // Load 2FA config on mount
  useEffect(() => {
    const load2FAConfig = async () => {
      try {
        const { getAppConfigService } = await import('@/domain/app-config')
        const service = getAppConfigService()
        const config = await service.getAppConfig()
        if (config?.featureFlags) {
          setTwoFactorConfig({
            enable2FA: config.featureFlags.enable2FA || false,
            require2FAForAdmin: config.featureFlags.require2FAForAdmin || false,
          })
        }
      } catch (error) {
        console.error('[Profile] Load 2FA config error:', error)
      }
    }
    load2FAConfig()
  }, [])

  // Get auth context
  // Load user data on mount
  useEffect(() => {
    const loadUserData = async () => {
      if (authUser) {
        // ✅ Fetch 2FA status จาก Cloud Function (ไม่ใช่แค่จาก authUser)
        let twoFactorEnabled = false
        try {
          const twoFactorCheck = await TwoFactorAuthService.checkRequirement(authUser.id)
          twoFactorEnabled = twoFactorCheck.isEnabled || false
          console.log('[Profile] 2FA status loaded:', { isEnabled: twoFactorEnabled, userId: authUser.id })
        } catch (error) {
          console.error('[Profile] Failed to load 2FA status:', error)
          // Fallback to authUser.twoFactorEnabled
          twoFactorEnabled = authUser.twoFactorEnabled || false
        }
        
        setUserData({
          id: authUser.id,
          firstName: authUser.firstName || '',
          lastName: authUser.lastName || '',
          username: authUser.username || '',
          email: authUser.email || '',
          phone: authUser.phone || '',
          lineId: authUser.lineId || '',
          role: getUserTypeDisplay(authUser.type),
          status: authUser.status || 'Active',
          avatar: authUser.photo || null,
          createdAt: authUser.createdAt ? new Date(authUser.createdAt).toLocaleDateString('th-TH') : '',
          updatedAt: authUser.updatedAt ? new Date(authUser.updatedAt).toLocaleDateString('th-TH') : '',
          lastLogin: new Date().toLocaleString('th-TH'),
          twoFactorEnabled: twoFactorEnabled,
          emailNotifications: true,
          pushNotifications: true,
          smsNotifications: false,
          language: 'th',
          timezone: 'Asia/Bangkok',
        })
      }
    }
    loadUserData()
  }, [authUser])

  // Device and Login History state
  const [devices, setDevices] = useState<UserDevice[]>([])
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([])
  const [isLoadingDevices, setIsLoadingDevices] = useState(false)
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null)

  // Load devices and login history
  useEffect(() => {
    async function loadDevicesAndHistory() {
      if (!sessionToken || activeTab !== 'sessions') return

      setIsLoadingDevices(true)
      try {
        // Get current device ID from sessionStorage
        const storedDeviceId = sessionStorage.getItem('current_device_id')
        setCurrentDeviceId(storedDeviceId)

        // Load devices
        const devicesData = await DeviceService.getUserDevices(sessionToken)
        setDevices(devicesData)

        // Load login history
        const historyData = await DeviceService.getLoginHistory(sessionToken, undefined, { limit: 20 })
        setLoginHistory(historyData)
      } catch (error) {
        console.error('❌ Failed to load devices/history:', error)
      } finally {
        setIsLoadingDevices(false)
      }
    }

    loadDevicesAndHistory()
  }, [sessionToken, activeTab])

  // Format device name for display
  const formatDeviceName = (device: UserDevice) => {
    return `${device.os} - ${device.browser}`
  }

  // Format date for display
  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Format relative time
  const formatRelativeTime = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'เมื่อสักครู่'
    if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`
    if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`
    if (diffDays < 7) return `${diffDays} วันที่แล้ว`
    return formatDate(d)
  }

  // ✅ Function สำหรับสร้าง QR Code (เรียกใช้เมื่อเปิด dialog)
  const handleGenerateQRCode = async () => {
    try {
      console.log('[Profile] Generating QR Code...', { userId: userData.id, hasSessionToken: !!sessionToken })
      setIsGeneratingQR(true)
      setTwoFactorError(null)
      if (!sessionToken) {
        const errorMsg = 'ไม่พบ Session Token'
        console.error('[Profile] QR Code generation failed:', errorMsg)
        setTwoFactorError(errorMsg)
        setIsGeneratingQR(false)
        return
      }
      if (!userData.id) {
        const errorMsg = 'ไม่พบ User ID'
        console.error('[Profile] QR Code generation failed:', errorMsg)
        setTwoFactorError(errorMsg)
        setIsGeneratingQR(false)
        return
      }
      console.log('[Profile] Calling TwoFactorAuthService.generateQRCode...')
      const result = await TwoFactorAuthService.generateQRCode(sessionToken, userData.id)
      console.log('[Profile] QR Code generation result:', { success: result.success, hasQRCode: !!result.qrCode, hasSecret: !!result.secret, error: result.error })
      if (result.success && result.qrCode && result.secret) {
        setTwoFactorQRCode(result.qrCode)
        setTwoFactorSecret(result.secret)
        console.log('[Profile] QR Code generated successfully')
      } else {
        const errorMsg = result.error || 'ไม่สามารถสร้าง QR Code ได้'
        console.error('[Profile] QR Code generation failed:', errorMsg)
        setTwoFactorError(errorMsg)
      }
    } catch (err: any) {
      const errorMsg = err.message || 'เกิดข้อผิดพลาด'
      console.error('[Profile] QR Code generation exception:', err)
      setTwoFactorError(errorMsg)
    } finally {
      setIsGeneratingQR(false)
    }
  }

  // ✅ Auto-generate QR Code เมื่อเปิด dialog (สำหรับ enable)
  useEffect(() => {
    if (showEnable2FADialog && !userData.twoFactorEnabled && !twoFactorQRCode && !isGeneratingQR && sessionToken && userData.id) {
      console.log('[Profile] Dialog opened, auto-generating QR Code...', { 
        showDialog: showEnable2FADialog, 
        twoFactorEnabled: userData.twoFactorEnabled, 
        hasQRCode: !!twoFactorQRCode,
        isGenerating: isGeneratingQR,
        hasSessionToken: !!sessionToken,
        userId: userData.id
      })
      handleGenerateQRCode()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEnable2FADialog, userData.twoFactorEnabled, twoFactorQRCode, isGeneratingQR, sessionToken, userData.id])

  // เปิด confirm dialog ก่อนบันทึก Profile
  const handleSaveClick = () => {
    setShowConfirmSave(true)
  }
  
  // บันทึก Profile จริงหลังยืนยัน
  const handleConfirmSaveProfile = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      console.log('🔐 Save Profile Debug:', {
        hasSessionToken: !!sessionToken,
        sessionTokenLength: sessionToken?.length,
        userId: userData.id,
        userData: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone,
          email: userData.email,
        }
      });
      
      if (!sessionToken || !userData.id) {
        throw new Error('Session not found');
      }
      
      const service = await getUserService();
      await service.updateAdmin(sessionToken, userData.id, {
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        lineId: userData.lineId,
        email: userData.email,
      });
      
      setShowConfirmSave(false);
      setSaveMessage({ type: 'success', text: t('profile.saveSuccess') });
      setIsEditing(false);
      
      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
      
    } catch (error: any) {
      console.error('Save profile error:', error);
      setSaveMessage({ type: 'error', text: error.message || t('profile.saveFailed') });
    } finally {
      setIsSaving(false);
    }
  }
  
  // คำนวณการเปลี่ยนแปลง Profile
  const getProfileChanges = () => {
    const changes: { field: string; oldValue?: string; newValue?: string }[] = []
    const original = authUser // จาก auth context
    
    if (original && userData.firstName !== original.firstName) {
      changes.push({ field: 'ชื่อ', oldValue: original.firstName, newValue: userData.firstName })
    }
    if (original && userData.lastName !== original.lastName) {
      changes.push({ field: 'นามสกุล', oldValue: original.lastName, newValue: userData.lastName })
    }
    if (original && userData.phone !== original.phone) {
      changes.push({ field: 'เบอร์โทร', oldValue: original.phone, newValue: userData.phone })
    }
    if (original && userData.email !== original.email) {
      changes.push({ field: 'อีเมล', oldValue: original.email, newValue: userData.email })
    }
    
    return changes
  }
  
  const handleChangePassword = async () => {
    setPasswordError(null);
    
    // Validate
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError(t('profile.allFieldsRequired'));
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t('profile.passwordMismatch'));
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError(t('profile.passwordTooShort'));
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      if (!sessionToken || !userData.id) {
        throw new Error('Session not found');
      }
      
      const service = await getUserService();
      
      // Use dedicated changePassword method instead of updateAdmin
      await service.changePassword(sessionToken, userData.id, passwordData.newPassword);
      
      // Clear form and close dialog
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowChangePasswordDialog(false);
      setSaveMessage({ type: 'success', text: t('profile.passwordChanged') });
      
      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
      
    } catch (error: any) {
      console.error('Change password error:', error);
      setPasswordError(error.message || t('profile.passwordChangeFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  }

  return (
    <div className="space-y-6 overflow-x-hidden max-w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <User className="h-6 w-6 text-primary" />
            {t('profile.title')}
          </h1>
          <p className="text-muted-foreground">{t('profile.subtitle')}</p>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={cn(
          "flex items-center gap-2 p-3 rounded-lg text-sm",
          saveMessage.type === 'success' ? "bg-win/10 text-win border border-win/20" : "bg-destructive/10 text-destructive border border-destructive/20"
        )}>
          {saveMessage.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {saveMessage.text}
        </div>
      )}

      {/* Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                {userData.avatar ? (
                  <img 
                    src={userData.avatar} 
                    alt="Profile" 
                    className="h-32 w-32 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-32 w-32 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-4xl font-bold text-primary-foreground">
                    {userData.firstName?.[0] || 'A'}{userData.lastName?.[0] || 'U'}
                  </div>
                )}
                <button className="absolute bottom-0 right-0 p-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                  <Camera className="h-4 w-4" />
                </button>
              </div>
              <div className="text-center">
                <h2 className="text-xl font-semibold">{userData.firstName || userData.username} {userData.lastName}</h2>
                <p className="text-sm text-muted-foreground">@{userData.username}</p>
                <Badge variant="secondary" className="mt-1">
                  <Shield className="h-3 w-3 mr-1" />
                  {userData.role}
                </Badge>
              </div>
            </div>

            {/* Info Section */}
            <div className="flex-1 grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4" /> {t('profile.email')}
                </p>
                <p className="font-medium">{userData.email || '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Phone className="h-4 w-4" /> {t('profile.phone')}
                </p>
                <p className="font-medium">{userData.phone || '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">LINE ID</p>
                <p className="font-medium">{userData.lineId || '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('profile.status')}</p>
                <Badge variant={userData.status === 'Active' ? 'default' : 'secondary'} className={cn(
                  "border-0",
                  userData.status === 'Active' ? "bg-win/10 text-win" : "bg-amber-500/10 text-amber-500"
                )}>
                  {userData.status}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('profile.memberSince')}</p>
                <p className="font-medium">{userData.createdAt || '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('profile.lastUpdated')}</p>
                <p className="font-medium">{userData.updatedAt || '-'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="general" className="gap-2 flex-shrink-0">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">{t('profile.tabs.general')}</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2 flex-shrink-0">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">{t('profile.tabs.security')}</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2 flex-shrink-0">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">{t('profile.tabs.notifications')}</span>
          </TabsTrigger>
          <TabsTrigger value="sessions" className="gap-2 flex-shrink-0">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">{t('profile.tabs.sessions')}</span>
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{t('profile.personalInfo')}</CardTitle>
                  <CardDescription>{t('profile.personalInfoDesc')}</CardDescription>
                </div>
                {!isEditing ? (
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="gap-2">
                    <Edit className="h-4 w-4" />
                    {t('common.edit')}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={isSaving}>
                      {t('common.cancel')}
                    </Button>
                    <Button size="sm" onClick={handleSaveClick} disabled={isSaving} className="gap-2">
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {isSaving ? t('common.saving') : t('common.save')}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('profile.username')}</Label>
                  <Input 
                    value={userData.username}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">{t('profile.usernameCannotChange')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.userType')}</Label>
                  <Input 
                    value={userData.role}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.firstName')}</Label>
                  <Input 
                    value={userData.firstName} 
                    onChange={(e) => setUserData({...userData, firstName: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.lastName')}</Label>
                  <Input 
                    value={userData.lastName}
                    onChange={(e) => setUserData({...userData, lastName: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.email')}</Label>
                  <Input 
                    type="email" 
                    value={userData.email}
                    onChange={(e) => setUserData({...userData, email: e.target.value})}
                    disabled={!isEditing}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.phone')}</Label>
                  <Input 
                    value={userData.phone}
                    onChange={(e) => setUserData({...userData, phone: e.target.value})}
                    disabled={!isEditing}
                    placeholder="081-234-5678"
                  />
                </div>
                <div className="space-y-2">
                  <Label>LINE ID</Label>
                  <Input 
                    value={userData.lineId}
                    onChange={(e) => setUserData({...userData, lineId: e.target.value})}
                    disabled={!isEditing}
                    placeholder="@lineid"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('profile.language')}</Label>
                  <Select 
                    value={userData.language} 
                    onValueChange={(value) => setUserData({...userData, language: value})}
                    disabled={!isEditing}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="th">🇹🇭 ไทย</SelectItem>
                      <SelectItem value="en">🇬🇧 English</SelectItem>
                      <SelectItem value="lo">🇱🇦 ລາວ</SelectItem>
                      <SelectItem value="km">🇰🇭 ខ្មែរ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.timezone')}</Label>
                  <Select 
                    value={userData.timezone}
                    onValueChange={(value) => setUserData({...userData, timezone: value})}
                    disabled={!isEditing}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Bangkok">Asia/Bangkok (GMT+7)</SelectItem>
                      <SelectItem value="Asia/Vientiane">Asia/Vientiane (GMT+7)</SelectItem>
                      <SelectItem value="Asia/Phnom_Penh">Asia/Phnom_Penh (GMT+7)</SelectItem>
                      <SelectItem value="Asia/Singapore">Asia/Singapore (GMT+8)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                {t('profile.password')}
              </CardTitle>
              <CardDescription>{t('profile.passwordDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => setShowChangePasswordDialog(true)} className="gap-2">
                <Lock className="h-4 w-4" />
                {t('profile.changePassword')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                {t('profile.twoFactorAuth')}
              </CardTitle>
              <CardDescription>{t('profile.twoFactorDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  {userData.twoFactorEnabled ? (
                    <CheckCircle className="h-8 w-8 text-win" />
                  ) : (
                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium">
                      {userData.twoFactorEnabled ? t('profile.twoFactorActive') : t('profile.twoFactorInactive')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {userData.twoFactorEnabled ? t('profile.twoFactorActiveDesc') : t('profile.twoFactorInactiveDesc')}
                    </p>
                  </div>
                </div>
                <Button 
                  variant={userData.twoFactorEnabled ? "outline" : "default"}
                  onClick={() => setShowEnable2FADialog(true)}
                >
                  {userData.twoFactorEnabled ? t('profile.disable') : t('profile.enable')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('profile.notificationSettings')}</CardTitle>
              <CardDescription>{t('profile.notificationSettingsDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t('profile.emailNotifications')}</p>
                    <p className="text-sm text-muted-foreground">{t('profile.emailNotificationsDesc')}</p>
                  </div>
                </div>
                <Switch 
                  checked={userData.emailNotifications}
                  onCheckedChange={(checked) => setUserData({...userData, emailNotifications: checked})}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t('profile.pushNotifications')}</p>
                    <p className="text-sm text-muted-foreground">{t('profile.pushNotificationsDesc')}</p>
                  </div>
                </div>
                <Switch 
                  checked={userData.pushNotifications}
                  onCheckedChange={(checked) => setUserData({...userData, pushNotifications: checked})}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t('profile.smsNotifications')}</p>
                    <p className="text-sm text-muted-foreground">{t('profile.smsNotificationsDesc')}</p>
                  </div>
                </div>
                <Switch 
                  checked={userData.smsNotifications}
                  onCheckedChange={(checked) => setUserData({...userData, smsNotifications: checked})}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-4">
          {/* Devices Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                อุปกรณ์ที่ใช้งาน ({devices.length})
              </CardTitle>
              <CardDescription>
                รายการอุปกรณ์ที่คุณใช้ login เข้าระบบ
                {devices.length > 0 && (
                  <span className="ml-2 text-xs">
                    (เครื่องเดียวกันจะมี fingerprint_id เหมือนกัน)
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoadingDevices ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : devices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Smartphone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>ยังไม่มีข้อมูลอุปกรณ์</p>
                  <p className="text-xs mt-1">ข้อมูลจะแสดงหลังจาก login</p>
                </div>
              ) : (
                devices.map((device) => {
                  const isCurrentDevice = device.id === currentDeviceId
                  return (
                    <div
                      key={device.id}
                      className={cn(
                        "flex items-center justify-between p-4 rounded-lg border",
                        isCurrentDevice
                          ? "bg-primary/5 border-primary/20"
                          : "bg-muted/30 border-border"
                      )}
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className={cn(
                          "p-2 rounded-lg",
                          isCurrentDevice ? "bg-primary/10" : "bg-muted"
                        )}>
                          <Smartphone className={cn(
                            "h-5 w-5",
                            isCurrentDevice ? "text-primary" : "text-muted-foreground"
                          )} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{formatDeviceName(device)}</p>
                            {isCurrentDevice && (
                              <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-0">
                                เครื่องปัจจุบัน
                              </Badge>
                            )}
                            {device.isTrusted && (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                ไว้ใจแล้ว
                              </Badge>
                            )}
                            {device.isBlocked && (
                              <Badge variant="destructive" className="text-xs">
                                ถูกบล็อก
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 space-y-1">
                            <p className="text-sm text-muted-foreground">
                              <span className="font-mono text-xs">{device.fingerprintId.substring(0, 16)}...</span>
                              {' • '}
                              {device.deviceType === 'desktop' ? '🖥️ Desktop' : device.deviceType === 'mobile' ? '📱 Mobile' : '📱 Tablet'}
                              {' • '}
                              {device.screenResolution}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {device.ipAddress && `${device.ipAddress} • `}
                              เข้าใช้งาน {device.loginCount} ครั้ง
                              {' • '}
                              ครั้งแรก: {formatDate(device.firstSeenAt)}
                              {' • '}
                              ล่าสุด: {formatRelativeTime(device.lastSeenAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!device.isTrusted && !device.isBlocked && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!sessionToken) return
                              try {
                                await DeviceService.trustDevice(sessionToken, device.id)
                                // Reload devices
                                const updatedDevices = await DeviceService.getUserDevices(sessionToken)
                                setDevices(updatedDevices)
                              } catch (error) {
                                console.error('Failed to trust device:', error)
                              }
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            ไว้ใจ
                          </Button>
                        )}
                        {!device.isBlocked && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={async () => {
                              if (!sessionToken) return
                              if (!confirm('คุณแน่ใจหรือไม่ว่าต้องการบล็อกอุปกรณ์นี้?')) return
                              try {
                                await DeviceService.blockDevice(sessionToken, device.id)
                                // Reload devices
                                const updatedDevices = await DeviceService.getUserDevices(sessionToken)
                                setDevices(updatedDevices)
                              } catch (error) {
                                console.error('Failed to block device:', error)
                              }
                            }}
                          >
                            บล็อก
                          </Button>
                        )}
                        {device.isBlocked && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!sessionToken) return
                              try {
                                await DeviceService.unblockDevice(sessionToken, device.id)
                                // Reload devices
                                const updatedDevices = await DeviceService.getUserDevices(sessionToken)
                                setDevices(updatedDevices)
                              } catch (error) {
                                console.error('Failed to unblock device:', error)
                              }
                            }}
                          >
                            ยกเลิกบล็อก
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </CardContent>
          </Card>

          {/* Login History Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="h-5 w-5" />
                ประวัติการ Login ({loginHistory.length})
              </CardTitle>
              <CardDescription>
                รายการการ login ทั้งหมดของคุณ
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingDevices ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : loginHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>ยังไม่มีประวัติการ login</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {loginHistory.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 text-sm"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {log.success ? (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {log.device
                              ? `${log.device.os} - ${log.device.browser}`
                              : 'Unknown Device'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {log.ipAddress || 'N/A'}
                            {' • '}
                            {log.loginMethod === 'password' ? '🔑 Password' : log.loginMethod === 'otp' ? '📱 OTP' : '🌐 Social'}
                            {log.device && (
                              <>
                                {' • '}
                                <span className="font-mono text-xs">
                                  {log.device.deviceType === 'desktop' ? '🖥️' : log.device.deviceType === 'mobile' ? '📱' : '📱'}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p
                          className={cn(
                            "text-xs font-medium",
                            log.success ? "text-green-600" : "text-red-600"
                          )}
                        >
                          {log.success ? "✅ สำเร็จ" : "❌ ล้มเหลว"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(log.createdAt)}
                        </p>
                        {log.failureReason && (
                          <p className="text-xs text-red-600 mt-1">
                            {log.failureReason}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={(open) => {
        setShowChangePasswordDialog(open);
        if (!open) {
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setPasswordError(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              {t('profile.changePassword')}
            </DialogTitle>
            <DialogDescription>{t('profile.changePasswordDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('profile.currentPassword')}</Label>
              <div className="relative">
                <Input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="••••••••"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  disabled={isChangingPassword}
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('profile.newPassword')}</Label>
              <Input 
                type="password" 
                placeholder="••••••••"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                disabled={isChangingPassword}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('profile.confirmPassword')}</Label>
              <Input 
                type="password" 
                placeholder="••••••••"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                disabled={isChangingPassword}
              />
            </div>
            
            {passwordError && (
              <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{passwordError}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePasswordDialog(false)} disabled={isChangingPassword}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleChangePassword} disabled={isChangingPassword} className="gap-2">
              {isChangingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t('profile.updatePassword')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enable 2FA Dialog */}
      <Dialog open={showEnable2FADialog} onOpenChange={(open) => {
        setShowEnable2FADialog(open)
        if (!open) {
          // Reset state when closing
          setTwoFactorQRCode(null)
          setTwoFactorSecret(null)
          setTwoFactorVerificationCode('')
          setTwoFactorError(null)
        } else if (!userData.twoFactorEnabled && !twoFactorQRCode) {
          // ✅ Auto-generate QR Code เมื่อเปิด dialog (สำหรับ enable)
          handleGenerateQRCode()
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              {userData.twoFactorEnabled ? t('profile.disable2FA') : t('profile.enable2FA')}
            </DialogTitle>
            <DialogDescription>
              {userData.twoFactorEnabled ? t('profile.disable2FADesc') : t('profile.enable2FADesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {!userData.twoFactorEnabled && (
              <div className="space-y-4">
                {!twoFactorQRCode ? (
                  <div className="text-center space-y-4">
                    {isGeneratingQR ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                        <p className="text-sm text-muted-foreground">
                          กำลังสร้าง QR Code...
                        </p>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          กำลังสร้าง QR Code...
                        </p>
                        {twoFactorError && (
                          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm mt-2">
                            {twoFactorError}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="p-4 rounded-lg bg-muted/30 text-center">
                      <img 
                        src={twoFactorQRCode} 
                        alt="2FA QR Code" 
                        className="h-32 w-32 mx-auto mb-3"
                      />
                      <p className="text-sm text-muted-foreground">{t('profile.scanQRCode')}</p>
                      {twoFactorSecret && (
                        <p className="text-xs text-muted-foreground mt-2 font-mono break-all">
                          Manual Entry: {twoFactorSecret}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t('profile.verificationCode')}</Label>
                      <Input 
                        placeholder="000000" 
                        maxLength={6} 
                        className="text-center text-lg tracking-widest font-mono"
                        value={twoFactorVerificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                          setTwoFactorVerificationCode(value)
                          setTwoFactorError(null)
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        เปิดแอป Authenticator (Google Authenticator, Authy) และกรอกรหัส 6 หลัก
                      </p>
                    </div>
                  </>
                )}
                {twoFactorError && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                    {twoFactorError}
                  </div>
                )}
              </div>
            )}
            {userData.twoFactorEnabled && (
              <div className="space-y-4">
                {/* ✅ ตรวจสอบว่าเป็น Admin และ require2FAForAdmin = true → แสดงข้อความห้ามปิด */}
                {(() => {
                  const isAdmin = authUser?.type === 'admin' || authUser?.type === 'super_admin'
                  const isSuperAdmin = authUser?.type === 'super_admin'
                  const canDisable = !isAdmin || !twoFactorConfig.require2FAForAdmin || isSuperAdmin
                  
                  if (!canDisable) {
                    return (
                      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-center">
                        <AlertTriangle className="h-12 w-12 mx-auto text-amber-600 mb-2" />
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                          ไม่สามารถปิดการใช้งาน 2FA ได้
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          ระบบบังคับให้ Admin ต้องใช้ 2FA เพื่อความปลอดภัย<br />
                          Super Admin สามารถปิดได้ใน Settings → Security
                        </p>
                      </div>
                    )
                  }
                  
                  return (
                    <>
                      <div className="p-4 rounded-lg bg-destructive/10 text-center">
                        <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-2" />
                        <p className="text-sm text-destructive font-medium">{t('profile.disable2FAWarning')}</p>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('profile.verificationCode')}</Label>
                        <Input 
                          type="text"
                          placeholder="000000" 
                          maxLength={6}
                          className="text-center text-lg tracking-widest font-mono"
                          value={twoFactorVerificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                            setTwoFactorVerificationCode(value)
                            setTwoFactorError(null)
                          }}
                        />
                        <p className="text-xs text-muted-foreground">
                          กรอกรหัส 6 หลักจากแอป Authenticator เพื่อยืนยันการปิดใช้งาน
                        </p>
                      </div>
                      {twoFactorError && (
                        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                          {twoFactorError}
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnable2FADialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant={userData.twoFactorEnabled ? "destructive" : "default"}
              className="gap-2"
              disabled={
                (!userData.twoFactorEnabled && !twoFactorQRCode) ||
                twoFactorVerificationCode.length !== 6 ||
                isVerifying2FA ||
                (userData.twoFactorEnabled && (() => {
                  const isAdmin = authUser?.type === 'admin' || authUser?.type === 'super_admin'
                  const isSuperAdmin = authUser?.type === 'super_admin'
                  return isAdmin && twoFactorConfig.require2FAForAdmin && !isSuperAdmin
                })())
              }
              onClick={async () => {
                try {
                  setIsVerifying2FA(true)
                  setTwoFactorError(null)
                  if (!sessionToken) {
                    setTwoFactorError('ไม่พบ Session Token')
                    return
                  }
                  
                  if (userData.twoFactorEnabled) {
                    // Disable 2FA
                    const result = await TwoFactorAuthService.disable(
                      sessionToken,
                      userData.id,
                      twoFactorVerificationCode
                    )
                    if (result.success) {
                      setUserData({ ...userData, twoFactorEnabled: false })
                      setShowEnable2FADialog(false)
                      setSaveMessage({ type: 'success', text: 'ปิดการใช้งาน 2FA สำเร็จ' })
                      // ✅ Refresh user data จาก auth context
                      if (refreshUser) {
                        await refreshUser()
                      }
                    } else {
                      setTwoFactorError(result.error || 'รหัสยืนยันไม่ถูกต้อง')
                    }
                  } else {
                    // Enable 2FA
                    const result = await TwoFactorAuthService.verifyAndEnable(
                      sessionToken,
                      userData.id,
                      twoFactorVerificationCode
                    )
                    if (result.success) {
                      setUserData({ ...userData, twoFactorEnabled: true })
                      setShowEnable2FADialog(false)
                      // ✅ Refresh user data จาก auth context
                      if (refreshUser) {
                        await refreshUser()
                      }
                      
                      // ✅ แสดงข้อความแจ้งเตือนก่อน logout
                      setSaveMessage({ 
                        type: 'success', 
                        text: 'เปิดการใช้งาน 2FA สำเร็จ ระบบจะออกจากระบบในอีกสักครู่เพื่อให้ทดสอบ 2FA' 
                      })
                      
                      // ✅ บังคับ logout เพื่อให้ทดลอง 2FA
                      setTimeout(async () => {
                        if (logout) {
                          await logout()
                        }
                      }, 3000)
                    } else {
                      setTwoFactorError(result.error || 'รหัสยืนยันไม่ถูกต้อง')
                    }
                  }
                } catch (err: any) {
                  setTwoFactorError(err.message || 'เกิดข้อผิดพลาด')
                } finally {
                  setIsVerifying2FA(false)
                }
              }}
            >
              {isVerifying2FA ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  กำลังตรวจสอบ...
                </>
              ) : (
                <>
                  {userData.twoFactorEnabled ? t('profile.disable') : t('profile.enable')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Save Profile Dialog */}
      <SaveConfirmDialog
        open={showConfirmSave}
        onOpenChange={setShowConfirmSave}
        onConfirm={handleConfirmSaveProfile}
        isLoading={isSaving}
        itemName="ข้อมูลโปรไฟล์"
        changes={getProfileChanges()}
      />
    </div>
  )
}



